/**
 * Public package-tracking API client.
 *
 * Talks to the public, no-auth endpoint:
 *   GET {BASE}/api/v1/tracking/public/:identifier
 * where `identifier` is a MAWB / AWB / box number.
 */
import type { JourneyResponse } from '../types/tracking';

// Create React App only exposes env vars prefixed `REACT_APP_` to the
// browser — so the API base must come from `REACT_APP_API_URL` in `.env`
// (a bare `API_URL` is never readable here). The fallback is the local
// backend; set `REACT_APP_API_URL` to the production URL for deploys.
const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000';

export type TrackingErrorKind = 'not_found' | 'network' | 'error';

/** A typed error so the page can show the right message per failure. */
export class TrackingError extends Error {
  kind: TrackingErrorKind;

  constructor(kind: TrackingErrorKind, message: string) {
    super(message);
    this.name = 'TrackingError';
    this.kind = kind;
  }
}

/**
 * Look up a shipment by its tracking identifier.
 *
 * @throws {TrackingError} with `kind`:
 *  - `'not_found'` — 404, no shipment matches the identifier
 *  - `'network'`   — request never reached the server
 *  - `'error'`     — any other non-2xx / unexpected response
 */
export async function trackShipment(
  identifier: string,
): Promise<JourneyResponse> {
  const id = identifier.trim();
  if (!id) {
    throw new TrackingError('not_found', 'No tracking number was entered.');
  }

  const url = `${BASE}/api/v1/tracking/public/${encodeURIComponent(id)}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new TrackingError(
      'network',
      'We could not reach the tracking service. Check your connection and try again.',
    );
  }

  if (res.status === 404) {
    throw new TrackingError(
      'not_found',
      'No shipment found for that tracking number.',
    );
  }

  if (!res.ok) {
    throw new TrackingError(
      'error',
      'Something went wrong while tracking your shipment. Please try again.',
    );
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new TrackingError(
      'error',
      'We received an unexpected response. Please try again.',
    );
  }

  // Standard envelope: { success, data: <JourneyResponse> }.
  const envelope = body as { data?: JourneyResponse } | JourneyResponse;
  const journey = (
    envelope && typeof envelope === 'object' && 'data' in envelope
      ? (envelope.data ?? envelope)
      : envelope
  ) as JourneyResponse;

  if (!journey || typeof journey !== 'object' || !journey.identifier) {
    throw new TrackingError(
      'not_found',
      'No shipment found for that tracking number.',
    );
  }

  return journey;
}
